const isObject = value => value && typeof value === 'object' && !Array.isArray(value)

const splitPostBody = postBody => {
    if (!isObject(postBody)) {
        return { filter: {}, frame: null, page: null }
    }

    const {
        frame,
        page,
        ...filter
    } = postBody

    return { filter, frame, page }
}

const resolveTemplate = (value, variables) => {
    if (typeof value !== 'string') return value

    const match = value.match(/^\{\{(.*?)\}\}$/)
    if (!match) return value

    return variables?.[match[1]]
}

const resolveTemplatesDeep = (value, variables) => {
    if (Array.isArray(value)) {
        return value.map(item => resolveTemplatesDeep(item, variables))
    }
    if (isObject(value)) {
        return Object.fromEntries(
            Object.entries(value).map(([key, item]) => [
                key,
                resolveTemplatesDeep(item, variables),
            ])
        )
    }
    return resolveTemplate(value, variables)
}

/**
 * Build the request used by TableWidget without mutating its shared config.
 * `widget.filter` historically represented extra top-level request options, so
 * those options are retained while its nested `filter` is merged explicitly.
 */
export const buildTableWidgetRequest = ({
    widget = {},
    filter = {},
    filter2,
    variables = {},
    userId,
    currentPage,
    totalCount,
    getCount = false,
    additionalFilter = {},
}) => {
    const legacyBody = isObject(widget.filter) ? widget.filter : {}
    const legacyFilter = isObject(legacyBody.filter) ? legacyBody.filter : {}
    const legacyOptions = { ...legacyBody }
    delete legacyOptions.filter
    const postBody = splitPostBody(widget.postBody)
    const frame = widget.frame || postBody.frame || widget.page || postBody.page
    const page = widget.page || postBody.page || frame
    const limit = widget.limit ?? 30

    const body = {
        ...legacyOptions,
        page,
        limit,
        filter: {
            ...legacyFilter,
            ...(isObject(filter) ? filter : {}),
            ...(isObject(additionalFilter) ? additionalFilter : {}),
            ...postBody.filter,
        },
    }

    if (frame) body.frame = frame
    if (filter2) body.filter2 = filter2
    if (currentPage != null) body.skip = currentPage
    if (widget.sort) body.sort = widget.sort
    if (widget.populate) body.populate = widget.populate
    if (Array.isArray(widget.populates)) body.populates = widget.populates
    if (totalCount == null || getCount) body.getCount = true

    if (variables.applet) {
        body.applet = variables.applet
        body.filter.applet = variables.applet
    }

    if (widget.self && userId) {
        body.filter[`values.${widget.self}`] = userId
    }

    return resolveTemplatesDeep(body, variables)
}

export const buildProcessWidgetRequest = ({
    widget = {},
    variables = {},
    userId,
    defaultFilter = {},
}) => {
    const postBody = splitPostBody(widget.postBody)
    const frame = widget.frame || postBody.frame || widget.page || postBody.page || 'forms'
    const page = widget.page || postBody.page || frame

    const body = {
        frame,
        page,
        filter: {
            ...defaultFilter,
            ...postBody.filter,
            frame,
        },
        limit: widget.limit ?? 1000,
        getCount: false,
    }

    if (widget.sort) body.sort = widget.sort
    if (Array.isArray(widget.populates)) body.populates = widget.populates

    if (variables.applet) {
        body.applet = variables.applet
        body.filter.applet = variables.applet
    }

    if (widget.self && userId) {
        body.filter[`values.${widget.self}`] = userId
    }

    return resolveTemplatesDeep(body, variables)
}
